import { Directive, HostBinding, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { NMollie } from '../interfaces';

/**
 *
 * Testing cards
 * @see https://docs.mollie.com/overview/testing#testing-different-types-of-cards
 *
 */
@Directive()
export abstract class MollieCardsPaymentComponentDef extends AbstractCheckoutPayment<NMollie.ICardsMethod> implements OnInit {
    private mollie?: NMollie.IMollieInstance;

    private initialized = false;

    @ViewChild('cardHolder', { static: false, read: ElementRef })
    protected cardHolder?: ElementRef<HTMLElement>;

    @ViewChild('cardHolderError', { static: false, read: ElementRef })
    protected cardHolderError?: ElementRef<HTMLElement>;

    @ViewChild('cardNumber', { static: false, read: ElementRef })
    protected cardNumber?: ElementRef<HTMLElement>;

    @ViewChild('cardNumberError', { static: false, read: ElementRef })
    protected cardNumberError?: ElementRef<HTMLElement>;

    @ViewChild('cardExpiry', { static: false, read: ElementRef })
    protected cardExpiry?: ElementRef<HTMLElement>;

    @ViewChild('cardExpiryError', { static: false, read: ElementRef })
    protected cardExpiryError?: ElementRef<HTMLElement>;

    @ViewChild('cardCvc', { static: false, read: ElementRef })
    protected cardCvc?: ElementRef<HTMLElement>;

    @ViewChild('cardCvcError', { static: false, read: ElementRef })
    protected cardCvcError?: ElementRef<HTMLElement>;

    private invalidElements = new Set<ElementRef<HTMLElement>>();

    protected isValid = false;

    protected get methodCode(): string {
        return NMollie.cardsMethodCode;
    }

    protected getJsAssets(): string[] {
        return [
            // 'https://code.jquery.com/jquery-3.6.4.slim.min.js',
            NMollie.scriptUrl
        ];
    }

    @HostBinding('hidden')
    protected get noFormNeeded(): boolean {
        return !this.method?.config.use_components;
    }

    override ngOnInit(): void {
        this.changeDetectorRef.detach();
        this.detectChanges();
        super.ngOnInit();
    }

    protected override onScriptLoadSuccess() {
        super.onScriptLoadSuccess();
        this.mountForm();
    }

    private mountForm() {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        if (!this.method?.config.use_components) {
            return;
        }

        if (!window.Mollie) {
            this.scriptStatus.error = 'Script initialization failed. Please use another payment method';
            return;
        }

        this.mollie = window.Mollie(this.method.config.profile_id, {
            testMode: this.method.isTest
        });

        [
            { type: 'cardHolder', element: this.cardHolder, errorElement: this.cardHolderError },
            { type: 'cardNumber', element: this.cardNumber, errorElement: this.cardNumberError },
            { type: 'expiryDate', element: this.cardExpiry, errorElement: this.cardExpiryError },
            { type: 'verificationCode', element: this.cardCvc, errorElement: this.cardCvcError }
        ].forEach(item => {
            if (!item.element || !item.errorElement) {
                return;
            }
            const component = this.mollie!.createComponent(item.type, this.getComponentOptions(item.type));
            this.invalidElements.add(item.element);
            component.mount(item.element.nativeElement);
            const errorElement = item.errorElement.nativeElement;
            component.addEventListener('change', event => {
                if (event.error && event.touched) {
                    errorElement.textContent = event.error;
                    errorElement.style.display = 'block';
                    if (!this.invalidElements.has(item.element!)) {
                        this.invalidElements.add(item.element!);
                        this.updateValidity();
                    }
                } else {
                    errorElement.textContent = '';
                    errorElement.style.display = 'none';
                    this.invalidElements.delete(item.element!);
                    this.updateValidity();
                }
            });
        });

        this.detectChanges();
    }

    /**
     * Get component options
     * @see https://docs.mollie.com/reference/mollie-js/mollie-object#example-for-cardholder-component
     */
    protected getComponentOptions(name: string): NMollie.IComponentOptions {
        return {};
    }

    protected updateValidity(): void {
        const valid = this.invalidElements.size === 0;
        if (this.isValid !== valid) {
            this.isValid = valid;
            this.detectChanges();
        }
    }

    protected override onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        checkoutSubmitBeforeEventData.continueSubmission = false;

        this.createToken()
            .then((token: string) => {
                checkoutSubmitBeforeEventData.data.paymentMethod = {
                    method: this.methodCode,
                    additional_data: {
                        card_token: token
                    }
                };
                checkoutSubmitBeforeEventData.next();
            })
            .catch(error => {
                this.checkoutFormComponent.status.error = error;
            });
    }

    private async createToken(): Promise<string> {
        if (!this.mollie) {
            throw new Error('Mollie instance is not initialized');
        }
        const result = await this.mollie.createToken();
        if (result.error) {
            throw new Error(result.error.message || 'Card authorization failed');
        }
        return result.token as string;
    }

    get isInitialized(): boolean {
        return this.initialized;
    }
}
